import { ProcessorOptions, Track } from "livekit-client";
import { getMicProcessingContext } from "../states/audioCtx";
import { createNoiseCancellation } from "./noiseCancellation";
import type { NoiseCancellation } from "./noiseCancellation";

// Имена артефактов пакета содержат хэш содержимого, их подставляет сборка
// пакета в блок ниже. Файлы неизменяемы, поэтому версии в query и фолбэк-копий
// не нужно: при обновлении пакета меняются обе строки и приложение
// пересобирается. Данные (модель и wasm) тоже хэш-именованы и лежат рядом с
// воркером в public/noise: их имена воркер резолвит относительно себя, фронт
// называет только ворклет и воркер.
// NOISE-ARTIFACTS:BEGIN — генерируется сборкой noise-supression/new_worker/build.mjs
const NOISE_WORKLET_URL = "/noise/noise-cancellation-audioWorklet.5b948240ea6f.js";
const NOISE_WORKER_URL = "/noise/noise-cancellation-worker.d2356ebc7bc1.js";
// NOISE-ARTIFACTS:END

// Размеры кадров движка считаются из расчёта 48кГц, ресемплинга нет. Поэтому
// микрофонный контекст создаётся с фиксированной частотой (audioCtx.ts), а
// здесь мы просто сверяем её — на другой частоте подавление не включаем.
const NS_SAMPLE_RATE = 48000;

export class AudioProcessor {
  name = "audio-vad-processor";
  processedTrack?: MediaStreamTrack;
  /**
   * Желаемое/фактическое состояние AI-шумодава — тумблер пользователя, а не
   * наличие узла ворклета в графе (узел есть всегда). Сбрасывается в false,
   * если движок не поднялся, — чтобы следующий проход пайплайна попробовал его
   * снова.
   */
  public noiseSuppressionEnabled: boolean = false;

  /**
   * Движок поднят и узел в графе живой: тумблер переключается одним setMode
   * (bypass/denoise), без пересоздания узла. False — движка в графе нет (узел
   * без движка либо прямой путь), поэтому включение обязано поднимать движок
   * заново.
   */
  private nsEngineMounted = false;

  private audioContext!: AudioContext;
  private sourceNode!: MediaStreamAudioSourceNode;
  private destinationNode!: MediaStreamAudioDestinationNode;
  private noise?: NoiseCancellation;
  private nsGeneration = 0;
  private muted: boolean = false;
  private destroyed: boolean = false;
  /**
   * Резолвится в destroy(). Нужен, чтобы ожидание готовности движка
   * (mountNoiseWorklet) заканчивалось вместе с процессором: гонка снимается
   * этим сигналом — то есть штатным завершением, а не отказом `ready`, который
   * увёл бы управление в ветку обработки ошибки. Сигнал пересоздаётся в
   * init(): restart() переиспользует экземпляр.
   */
  private destroySignal: Promise<void> = new Promise<void>(() => {});
  private signalDestroyed: () => void = () => {};

  constructor(noiseSuppressionEnabled: boolean) {
    this.noiseSuppressionEnabled = noiseSuppressionEnabled;
  }

  async init(opts: ProcessorOptions<Track.Kind>): Promise<void> {
    this.destroyed = false;
    // restart() переиспользует экземпляр на новом узле и новом движке:
    // состояние движка от прежнего узла сюда не переносится.
    this.nsEngineMounted = false;
    this.destroySignal = new Promise<void>((resolve) => {
      this.signalDestroyed = resolve;
    });
    const audioContext = getMicProcessingContext();
    if (!audioContext) {
      throw new Error(
        "AudioProcessor requires a microphone processing context (microphoneAudioContext)",
      );
    }
    if (opts.audioContext && opts.audioContext !== audioContext) {
      console.warn(
        "AudioProcessor ignores opts.audioContext: using the isolated microphone context",
      );
    }
    this.audioContext = audioContext;

    const mediaStream = new MediaStream([opts.track]);
    this.sourceNode = this.audioContext.createMediaStreamSource(mediaStream);
    this.destinationNode = this.audioContext.createMediaStreamDestination();

    // Ворклет ставится в граф ВСЕГДА: его mute — это и есть mute процессора
    // (правило §3.2), и он обязан существовать, даже когда AI-движок выключен.
    // Движок init поднимает только если он нужен прямо сейчас; дальше тумблер
    // переключает режим живого узла (setMode). Модуль ворклета кэширован на
    // контекст, а без движка ожидания нет — init остаётся быстрым.
    await this.mountNoiseWorklet(
      this.noiseSuppressionEnabled,
      ++this.nsGeneration,
    );

    this.processedTrack = this.destinationNode.stream.getAudioTracks()[0];
  }

  /**
   * Переключить AI-шумодав. Трек не пересоздаётся (constraints не зависят от
   * тумблера), а поднятый движок не пересоздаётся: у живого узла тумблер меняет
   * только режим выхода — при выключении ворклет перестаёт отправлять кадры
   * движку и отдаёт сырой вход на том же таймлайне, а движок с моделью остаются
   * загруженными и живыми. Если движка в графе нет, включение поднимает его
   * обычным путём: worker, модель, ожидание ready.
   */
  async setNoiseSuppressionEnabled(enable: boolean): Promise<void> {
    this.noiseSuppressionEnabled = enable;
    if (!this.sourceNode) return;
    if (this.nsEngineMounted && this.noise) {
      // Движок жив: узел, кольца и таймлайн остаются на месте, поэтому ни
      // подмены графа, ни нового поколения здесь не нужно — переключаем режим.
      this.noise.setMode(enable ? "denoise" : "bypass");
      return;
    }
    const generation = ++this.nsGeneration;
    await this.mountNoiseWorklet(enable, generation);
  }

  /**
   * Поставить в граф узел ворклета: с движком (`withEngine`) или без него.
   *
   * Узел без движка — вечный passthrough (loading у него навсегда true) и при
   * этом подчиняется mute; он же ставится на место узла с движком, когда
   * движок поднять не удалось. Тумблер этим путём не ходит: узел с живым
   * движком переключается режимом (setMode).
   *
   * Обмен графа идёт ДО ожидания готовности движка: свежий узел пропускает
   * вход как есть, поэтому подмена слышимой паузы не даёт.
   */
  private async mountNoiseWorklet(
    withEngine: boolean,
    generation: number,
  ): Promise<void> {
    if (this.audioContext.sampleRate !== NS_SAMPLE_RATE) {
      console.warn(
        `Noise suppression expects ${NS_SAMPLE_RATE}Hz, got ${this.audioContext.sampleRate}Hz; noise suppression skipped`,
      );
      // Узел на другой частоте работать не может. Если он ещё не смонтирован,
      // откатываемся на прямой путь; уже смонтированный не трогаем — иначе
      // пропал бы процессорный mute, который сейчас работает.
      this.nsEngineMounted = false;
      if (!this.noise) this.connectDirect();
      return;
    }

    let noise: NoiseCancellation;
    try {
      noise = await createNoiseCancellation({
        context: this.audioContext,
        workletUrl: NOISE_WORKLET_URL,
        // Без workerUrl движок не создаётся вообще: узел — вечный passthrough.
        workerUrl: withEngine ? NOISE_WORKER_URL : undefined,
        onError: (error) => this.onEngineLost(error),
      });
    } catch (error) {
      // Не поднялся сам модуль ворклета. Если узла ещё нет, прямого пути
      // достаточно (процессорного mute там нет — остаётся mute трека LiveKit);
      // если узел уже смонтирован, не трогаем его: mute продолжает работать.
      console.error("Failed to create noise cancellation worklet", error);
      this.nsEngineMounted = false;
      if (!this.noise) this.connectDirect();
      return;
    }

    if (this.destroyed || generation !== this.nsGeneration) {
      // Пока создавался узел, шумодав переключили или процессор уничтожили.
      noise.destroy();
      return;
    }

    // Подмена узла без разрыва: новый уже создан, снимаем старый со входа и
    // гасим его только после этого; mute переносится на новый узел.
    this.disconnectSource();
    this.noise?.destroy();
    this.noise = noise;
    // Флаг отражает наличие ГОТОВОГО движка в графе: у узла без движка его нет
    // вовсе, а у узла с движком он появится ниже, после ready.
    this.nsEngineMounted = false;
    noise.setMuted(this.muted);
    this.sourceNode.connect(noise.node);
    noise.node.connect(this.destinationNode);

    if (!withEngine) {
      // Работать нечему: узел готов сразу (ready уже разрешён), passthrough.
      return;
    }

    try {
      // Ждём готовность, но не бесконечно: destroy() снимает ожидание сигналом,
      // а разрушение пайплайна (destroy или фатал) отклоняет `ready`.
      await Promise.race([noise.ready, this.destroySignal]);
    } catch (error) {
      // Отказ может прийти не от нашего движка: узел мог быть вытеснен более
      // новым маунтом (он уже поднял свой) или пайплайн уничтожен — тогда граф
      // и признак не трогаем.
      if (this.destroyed || generation !== this.nsGeneration) return;
      // Движок не поднялся, а его узел уже не умеет passthrough (модуль
      // разрушил пайплайн при фатале). Ставим на место свежий узел без
      // движка: mute переживает сбой, звук идёт через браузерный NS из
      // constraints, а сброшенный признак позволит следующему проходу
      // пайплайна попробовать движок заново.
      console.error("Failed to initialize noise suppression engine", error);
      this.nsEngineMounted = false;
      this.noiseSuppressionEnabled = false;
      await this.mountNoiseWorklet(false, generation);
      return;
    }
    if (this.destroyed || generation !== this.nsGeneration) return;
    // Движок готов и стоит в графе: дальше тумблер работает режимом.
    this.nsEngineMounted = true;
  }

  /**
   * Прямой путь: источник сразу в приёмник. Единственный случай — не удалось
   * поднять САМ модуль ворклета. Процессорного mute здесь нет вообще, остаётся
   * только mute трека LiveKit. Пока узел ворклета смонтирован, прямой путь не
   * нужен и не применяется.
   */
  private connectDirect(): void {
    this.disconnectSource();
    if (this.noise) {
      this.noise.destroy();
      this.noise = undefined;
    }
    this.nsEngineMounted = false;
    this.sourceNode.connect(this.destinationNode);
  }

  /** Снять все исходящие связи источника; узел может быть ещё не подключён. */
  private disconnectSource(): void {
    try {
      this.sourceNode.disconnect();
    } catch {
      // У источника ещё нет исходящих связей (init с включённым шумодавом
      // врезает узел ворклета до первого connect) — disconnect тогда бросает.
    }
  }

  /**
   * Фатальный сбой движка ПОСЛЕ готовности: модуль уже разрушил узел, поэтому
   * граф остаётся без живого узла и микрофон молчит. Прямой путь тут не
   * подходит — он лишил бы процессор mute. Немедленно ставим на место свежий
   * узел БЕЗ движка: он passthrough (звук идёт через браузерный NS из
   * constraints) и сохраняет mute процессора. Сброшенный признак позволит
   * следующему проходу пайплайна поднять движок заново.
   */
  private onEngineLost(error: unknown): void {
    if (this.destroyed) return;
    console.error("Noise suppression engine lost", error);
    this.noiseSuppressionEnabled = false;
    this.nsEngineMounted = false;
    // Замена узла асинхронна: до неё узел мёртв, поэтому запускаем сразу и не
    // ждём (setMuted за это время — no-op у уже уничтоженного узла).
    void this.mountNoiseWorklet(false, this.nsGeneration);
  }

  setMuted(muted: boolean) {
    this.muted = muted;
    // Узел ворклета есть всегда, когда модуль ворклета вообще смонтировался
    // (в том числе при выключенном AI-шумодаве), поэтому mute процессора
    // срабатывает независимо от тумблера. Единственное исключение — прямой
    // путь, когда сам модуль ворклета не поднялся.
    this.noise?.setMuted(muted);
  }

  async restart(opts: ProcessorOptions<Track.Kind>): Promise<void> {
    await this.destroy();
    await this.init(opts);
  }

  async destroy(): Promise<void> {
    // Идемпотентность: protect от повторного destroy (например, ended-хендлер
    // и cleanup могут сработать друг за другом).
    if (this.destroyed) return;
    this.destroyed = true;
    this.nsGeneration++;
    this.nsEngineMounted = false;
    this.signalDestroyed();
    if (this.sourceNode) {
      this.disconnectSource();
    }
    if (this.noise) {
      this.noise.destroy();
      this.noise = undefined;
    }
    if (this.destinationNode) {
      this.destinationNode.disconnect();
    }
    if (this.processedTrack) {
      this.processedTrack.stop();
    }
  }
}
